import { useCallback, useEffect, useRef } from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { usePrevious } from 'hooks/use-previous';
import { useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { useGeolocation } from 'state/geolocation';
import { convertMilesToKilometers } from 'utils/distance/conversions';
import logger from 'utils/logger';
import { coordinateDistance } from 'utils/rbi-common';

import {
  StoreLocatorAction,
  StoreLocatorActionType,
  UseStoreLocatorInitProps,
} from '../components/types';
import { IStore } from '../ducks/stores';

const DEFAULT_DISTANCE_THRESHOLD_KM = 5;
const LOCATION_UPDATE_DEBOUNCE_MS = 1000;

/**
 * A hook that handles the initialization of the store locator map and list view.
 * - Focuses the map on the appropriate coordinates (active store, user location, or default)
 * - Highlights the active store in the list if one exists
 * - Scrolls the list view to the active store
 * - This hook is triggered when the user navigates away from the app and then returns
 *  ie: when the user changes the permission
 * - Monitors user location changes and triggers new searches when user moves significantly
 */
export const useStoreLocatorInit = ({
  mapRef,
  activeStore,
  nearbyStore,
  onRestaurantHighlight,
  actions,
  onSearchAllStores,
  onSearchNearbyAsync,
  forceNearbySearch = false,
  fallbackCoordinates = null,
}: UseStoreLocatorInitProps) => {
  const mapIsReady = mapRef.current?.mapIsReady;
  const hasCalledInitialLoad = useRef(false);
  const {
    loadCurrentPositionAsync,
    isPermissionGranted,
    isLoadingPermission,
    userCoordinates,
  } = useGeolocation();

  const lastIsPermissionGranted = useRef(isPermissionGranted);
  const previousUserCoordinates = usePrevious(userCoordinates);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleHighlightStore = useCallback(
    (coords?: ILocation, store?: IStore) => {
      const selectedStore = activeStore ?? nearbyStore ?? store;
      onRestaurantHighlight({ restaurant: selectedStore, coords });
    },
    [onRestaurantHighlight, activeStore, nearbyStore]
  );

  const shouldTriggerLocationSearch = useCallback(
    (currentCoords: ILocation | null, prevCoords: ILocation | null): boolean => {
      if (!currentCoords) {
        return false;
      }

      // Condition 1: Previous user location was null or doesn't exist. User may be in the process of granting permission.
      if (!prevCoords) {
        return true;
      }

      // Condition 2: New coordinates are 5km (or custom threshold) far from previous coordinates
      const distanceInMiles = coordinateDistance(
        { latitude: currentCoords.lat, longitude: currentCoords.lng },
        { latitude: prevCoords.lat, longitude: prevCoords.lng },
        true // return in miles
      );

      const distanceInKm = convertMilesToKilometers(distanceInMiles);
      return distanceInKm >= DEFAULT_DISTANCE_THRESHOLD_KM;
    },
    []
  );

  const searchAroundCoordinates = useCallback(
    async (coordinates: ILocation, selectedStore: IStore | null | undefined) => {
      mapRef.current?.focus({
        coordinates,
        focusReason: StoreLocatorAction.isInitial,
      });

      let nextSelected = selectedStore;
      if (!nextSelected || forceNearbySearch) {
        const nearbyStores = await onSearchNearbyAsync(coordinates);
        if (!nextSelected) {
          nextSelected = nearbyStores?.[0];
        }
      }
      onSearchAllStores(coordinates);
      handleHighlightStore(coordinates, nextSelected ?? undefined);
    },
    [forceNearbySearch, handleHighlightStore, mapRef, onSearchAllStores, onSearchNearbyAsync]
  );

  const initializeWithoutLocation = useCallback(() => {
    if (fallbackCoordinates) {
      void searchAroundCoordinates(fallbackCoordinates, activeStore ?? nearbyStore);
      return;
    }
    onSearchAllStores();
    handleHighlightStore();
  }, [
    activeStore,
    fallbackCoordinates,
    handleHighlightStore,
    nearbyStore,
    onSearchAllStores,
    searchAroundCoordinates,
  ]);

  const initializeWithLocation = useCallback(async () => {
    const coordinates = await loadCurrentPositionAsync();
    const selectedStore = activeStore ?? nearbyStore;
    if (coordinates) {
      await searchAroundCoordinates(coordinates, selectedStore);
    } else {
      initializeWithoutLocation();
    }
  }, [
    activeStore,
    nearbyStore,
    loadCurrentPositionAsync,
    searchAroundCoordinates,
    initializeWithoutLocation,
  ]);

  const resetInitializationState = useCallback(() => {
    hasCalledInitialLoad.current = false;
    actions.current[StoreLocatorActionType.INITIAL_LOAD] = false;
  }, [actions]);

  const getActiveOrNearbyStore = useCallback(async () => {
    const existingStore = activeStore ?? nearbyStore;

    if (existingStore) {
      return existingStore;
    }

    if (!userCoordinates) {
      return null;
    }

    try {
      const nearbyStores = await onSearchNearbyAsync(userCoordinates);
      return nearbyStores?.[0] || null;
    } catch (error) {
      return null;
    }
  }, [activeStore, nearbyStore, userCoordinates, onSearchNearbyAsync]);

  useEffect(() => {
    // Only run when the map is ready and we know the permission state
    if (!mapIsReady || isLoadingPermission) {
      return;
    }

    const permissionChanged = lastIsPermissionGranted.current !== isPermissionGranted;

    // If we've already performed the initial load and permission didn't change, do nothing
    if (hasCalledInitialLoad.current && !permissionChanged) {
      return;
    }

    actions.current[StoreLocatorActionType.INITIAL_LOAD] = true;
    hasCalledInitialLoad.current = true;
    lastIsPermissionGranted.current = isPermissionGranted;

    if (isPermissionGranted) {
      initializeWithLocation();
    } else {
      initializeWithoutLocation();
    }
  }, [
    mapIsReady,
    isLoadingPermission,
    isPermissionGranted,
    initializeWithLocation,
    initializeWithoutLocation,
    actions,
  ]);

  const handleUserLocationChange = useCallback(async () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      if (!userCoordinates || !isPermissionGranted) {
        return;
      }

      const shouldTrigger = shouldTriggerLocationSearch(
        userCoordinates,
        previousUserCoordinates ?? null
      );

      if (!shouldTrigger) {
        return;
      }

      try {
        const selectedStore = await getActiveOrNearbyStore();

        await Promise.all([
          onSearchAllStores(userCoordinates),
          selectedStore && handleHighlightStore(userCoordinates, selectedStore),
        ]);
      } catch (error) {
        logger.error({
          message: 'Failed to update location-based store data',
          error,
        });
      }
    }, LOCATION_UPDATE_DEBOUNCE_MS);
  }, [
    userCoordinates,
    isPermissionGranted,
    shouldTriggerLocationSearch,
    previousUserCoordinates,
    getActiveOrNearbyStore,
    onSearchAllStores,
    handleHighlightStore,
  ]);

  // Monitor user location changes and refresh store data when user moves significantly
  useEffect(() => {
    handleUserLocationChange();
  }, [handleUserLocationChange]);

  // Forces the initialization to reset if the app is not active
  useUpdateOnAppStateChange(async appState => {
    if (appState !== 'active') {
      resetInitializationState();
    }
  });

  // Reset initialization state when the component is unmounted
  useEffectOnUnmount(resetInitializationState);
};
